import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Spin, Tabs, Tag, Empty, message } from "antd";
import { format, fromUnixTime } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEnvelope, faUserSecret } from "@fortawesome/free-solid-svg-icons";
import authApi from "../../../api/authApi";
import { useAppSelector } from "../../../store";
import { canAccess } from "../../../config/admin-permissions";
import UserEmailModal from "./user-email-modal";
import UserDetailTable from "./user-detail-table";
import { buildUserDetailTabs } from "./user-detail-tabs-config";

const vnd = (v) => `${Number(v || 0).toLocaleString("vi-VN")}đ`;

/** Ô số liệu tài chính. */
function StatCard({ label, value, tone = "" }) {
    return (
        <div className="bg-surface border border-line rounded-lg px-3 py-2 min-w-[140px]">
            <p className="text-xs text-content-3">{label}</p>
            <p className={`font-semibold ${tone}`}>{value}</p>
        </div>
    );
}

/**
 * Chi tiết 1 thành viên: số liệu tài chính + 4 tab (đơn / rút tiền / thưởng / giới thiệu).
 *
 * Gom lại một chỗ để trả lời khiếu nại "sao tiền của tôi thiếu" mà không phải mở 4 màn
 * khác nhau rồi tự lọc userId. Thay cho php admin `dashboard-user.php`.
 *
 * Số liệu tài chính lấy từ /user/all_user?userId= — endpoint này đã tính sẵn đủ các
 * tổng (paid/pendingCash/withdraw/bonus/voucherSpent) nên không cần API mới.
 */
function UserDetail() {
    const { userId } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailTarget, setEmailTarget] = useState(null);

    const userInfo = useAppSelector((state) => state.auth.userInfo);
    const isAdmin = userInfo?.role === "admin";

    const fetchUser = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await authApi.getAllUser(`userId=${userId}&limit=1`);
            setUser(data.users?.[0] || null);
        } catch {
            message.error("Không tải được thông tin thành viên");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Ẩn tab mà CTV không có quyền gọi endpoint (khỏi bấm vào rồi ăn 403)
    const tabs = useMemo(
        () =>
            buildUserDetailTabs(userId).filter((t) =>
                (t.perms || []).some((p) => canAccess(userInfo, p))
            ),
        [userId, userInfo]
    );

    /** Login as user — lối vào hay dùng nhất khi đang xem chi tiết để hỗ trợ user */
    const loginAsUser = async () => {
        try {
            const res = await authApi.loginAsUser(user.userId);
            const base = process.env.REACT_APP_FRONTEND_URL || window.location.origin.replace(/:\d+$/, ":3000");
            window.open(`${base}/login-as?token=${encodeURIComponent(res.data.token)}&userId=${user.userId}`, "_blank");
        } catch (e) {
            message.error(e?.response?.data?.message || "Không thể đăng nhập với tư cách user này");
        }
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center p-10">
                <Spin />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-10">
                <Empty description={`Không tìm thấy thành viên #${userId}`} />
                <Link to="/user">← Về danh sách người dùng</Link>
            </div>
        );
    }

    const canLoginAs = isAdmin || !user.role || user.role === "user";
    const isActive = user.active === undefined ? true : Boolean(user.active);

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-3">
            <Link to="/user" className="text-sm">
                <FontAwesomeIcon icon={faArrowLeft} className="mr-1" /> Danh sách người dùng
            </Link>

            {/* Thông tin nhận dạng */}
            <div className="bg-surface border border-line rounded-lg p-4 flex flex-wrap gap-4 items-start shadow-sm">
                <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-xl font-bold m-0">{user.name || "(chưa có tên)"}</h1>
                        <span className="text-content-3">#{user.userId}</span>
                        {/* Cột active trên prod là TINYINT (schema đi qua migration tay) nên có
                            thể về 0/1 thay vì false/true -> so bằng truthiness như trang /user */}
                        <Tag color={isActive ? "green" : "red"}>{isActive ? "Hoạt động" : "Bị khóa"}</Tag>
                        {user.role === "admin" && <Tag color="volcano">Admin</Tag>}
                        {user.role === "mod" && <Tag color="blue">CTV/Mod</Tag>}
                        <Tag color={user.verified ? "green" : "default"}>
                            {user.verified ? "Đã xác thực" : "Chưa xác thực"}
                        </Tag>
                    </div>
                    <p className="mt-2 mb-0 font-mono text-sm">{user.email}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-3">
                        <span>
                            Đăng ký:{" "}
                            {user.createTime ? format(fromUnixTime(user.createTime), "dd/MM/yyyy HH:mm") : "—"}
                        </span>
                        <span>Nguồn: {user.registerSource || "—"}{user.registerChannel ? ` · ${user.registerChannel}` : ""}</span>
                        <span>
                            Người giới thiệu:{" "}
                            {user.ref ? <Link to={`/user/${user.ref}`}>#{user.ref}</Link> : "—"}
                        </span>
                        <span>IP đăng ký: {user.createByIp || "—"}</span>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {isAdmin && (
                        <Button
                            icon={<FontAwesomeIcon icon={faEnvelope} />}
                            onClick={() => setEmailTarget({ id: user.userId, name: user.name, email: user.email })}
                        >
                            Đổi email
                        </Button>
                    )}
                    {canLoginAs && (
                        <Button icon={<FontAwesomeIcon icon={faUserSecret} />} onClick={loginAsUser}>
                            Login as User
                        </Button>
                    )}
                </div>
            </div>

            {/* Số liệu tài chính */}
            <div className="flex flex-wrap gap-2">
                <StatCard label="Số dư hiện tại" value={vnd(user.cash)} tone="text-green-700" />
                <StatCard label="Chờ duyệt" value={vnd(user.pendingCash)} />
                <StatCard label="Chờ xác nhận" value={vnd(user.confirmCash)} />
                <StatCard label="Hoa hồng đã duyệt" value={vnd(user.paid)} />
                <StatCard label="Thưởng / trừ tiền" value={vnd(user.bonus)} />
                <StatCard label="Đã rút" value={vnd(user.withdraw)} />
                <StatCard label="Lệnh rút chờ xử lý" value={vnd(user.pendingWithdraw)} tone="text-orange-600" />
                <StatCard label="Đã đổi voucher" value={vnd(user.voucherSpent)} tone="text-purple-600" />
            </div>

            {tabs.length > 0 ? (
                <Tabs
                    className="bg-surface border border-line rounded-lg px-3 pb-3"
                    items={tabs.map((t) => ({
                        key: t.key,
                        label: t.label,
                        children: (
                            <UserDetailTable fetcher={t.fetcher} columns={t.columns} rowKey={t.rowKey} />
                        ),
                    }))}
                    destroyInactiveTabPane
                />
            ) : (
                <Empty description="Bạn không có quyền xem chi tiết đơn / rút tiền / thưởng của user" />
            )}

            <UserEmailModal
                user={emailTarget}
                onClose={() => setEmailTarget(null)}
                onSuccess={() => {
                    setEmailTarget(null);
                    fetchUser();
                }}
            />
        </div>
    );
}

export default UserDetail;
